/**
 * /app/ingredientes — BOM: lista y gestión de ingredientes/insumos.
 * Gateado por inventory_bom.
 */
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ClinicalPageShell } from "@/components/clinical";
import { DataTable, EmptyState, FilterBar, Header, Loading, QuickActions, StatCard } from "@/components/ds";
import type { DataTableColumn, QuickAction } from "@/components/ds";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FeatureLockedState } from "@/components/workspace/FeatureLockedState";
import { WorkspaceHubBack } from "@/components/workspace/WorkspaceHub";
import { CrudEditorShell } from "@/components/workspace/CrudEditorShell";
import { FieldLabel } from "@/components/help/FieldHelpTip";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Inbox, Plus, AlertTriangle, Boxes, Wallet, Pencil, Trash2 } from "lucide-react";
import { apiGetJson, apiSendJson, apiDelete } from "@/lib/sm-api";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import type { PlanApiErrorPayload } from "@structura/core/plan-api-errors";
import { toast } from "sonner";
import { useTenantContext } from "@/lib/tenant-context";
import { insumosCopy } from "@/lib/merchant-feature-copy";
import { useAppDialogs } from "@/components/ui/app-dialogs";

export const Route = createFileRoute("/app/ingredientes")({
  component: IngredientesPage,
  head: () => ({ meta: [{ title: "Insumos — Structura Market" }] }),
});

type Unit = "GRAMOS" | "KILOS" | "MILILITROS" | "LITROS" | "UNIDADES";
interface Ingredient {
  id: string;
  nombre: string;
  unidadBase: Unit;
  stockActual: number;
  costoUnitario: number;
  lowStockThreshold: number | null;
  active: boolean;
  brandId: string | null;
  brandName: string | null;
}

interface Brand {
  id: string;
  name: string;
}

interface Summary {
  total: number;
  totalValue: number;
  lowStockCount: number;
}

const UNIT_LABELS: Record<Unit, string> = {
  GRAMOS: "g",
  KILOS: "kg",
  MILILITROS: "ml",
  LITROS: "L",
  UNIDADES: "u",
};

const PAGE_SIZE = 25;

const fmt = (n: number, unit: Unit) =>
  `${new Intl.NumberFormat("es-AR", { maximumFractionDigits: 3 }).format(n)} ${UNIT_LABELS[unit]}`;

const pesos = (n: number) =>
  new Intl.NumberFormat("es-AR", { style: "currency", currency: "ARS", maximumFractionDigits: 4 }).format(n);

function IngredientesPage() {
  const tid = useWorkspaceTenantId();
  const navigate = useNavigate();
  const { businessProfile } = useTenantContext();
  const copy = insumosCopy(businessProfile);
  const [items, setItems] = useState<Ingredient[]>([]);
  const { confirm, prompt } = useAppDialogs();
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState<Summary>({ total: 0, totalValue: 0, lowStockCount: 0 });
  const [brands, setBrands] = useState<Brand[]>([]);
  const [loading, setLoading] = useState(true);
  const [lock, setLock] = useState<PlanApiErrorPayload | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState<Ingredient | null>(null);
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState("");
  const [brandFilter, setBrandFilter] = useState<string>("all");
  const [page, setPage] = useState(1);
  const [form, setForm] = useState({
    nombre: "",
    unidadBase: "UNIDADES" as Unit,
    costoUnitario: "",
    lowStockThreshold: "",
    brandId: "",
  });

  const load = async () => {
    if (!tid) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (query.trim()) params.set("search", query.trim());
      if (brandFilter !== "all") params.set("brandId", brandFilter);
      params.set("limit", String(PAGE_SIZE));
      params.set("offset", String((page - 1) * PAGE_SIZE));
      const r = (await apiGetJson(`/api/ingredients?${params.toString()}`, tid)) as {
        ingredients?: Ingredient[];
        total?: number;
        summary?: Summary;
      };
      setItems(r.ingredients ?? []);
      setTotal(r.total ?? 0);
      setSummary(r.summary ?? { total: 0, totalValue: 0, lowStockCount: 0 });
    } catch (e) {
      const err = e as Error & { planLock?: PlanApiErrorPayload };
      if (err.planLock) {
        setLock(err.planLock);
      } else {
        toast.error(`Error al cargar ${copy.itemLabelPlural.toLowerCase()}`);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!tid) return;
    void apiGetJson(`/api/brands`, tid)
      .then((r) => setBrands(Array.isArray(r) ? r : []))
      .catch(() => setBrands([]));
  }, [tid]);

  useEffect(() => {
    load();
  }, [tid, page, brandFilter]);

  // Búsqueda: reinicia a la página 1 y refetch con un pequeño debounce.
  useEffect(() => {
    if (!tid) return;
    setPage(1);
    const t = setTimeout(() => load(), 300);
    return () => clearTimeout(t);
  }, [query]);

  const handleCreate = async () => {
    if (!tid || !form.nombre.trim()) {
      toast.error("Nombre requerido");
      return;
    }
    setSaving(true);
    try {
      const r = (await apiSendJson(
        `/api/ingredients`,
        "POST",
        {
          nombre: form.nombre.trim(),
          unidadBase: form.unidadBase,
          costoUnitario: parseFloat(form.costoUnitario) || 0,
          lowStockThreshold: form.lowStockThreshold ? parseFloat(form.lowStockThreshold) : null,
          brandId: form.brandId || null,
        },
        tid,
      )) as { ingredient?: Ingredient; error?: string };
      if (r.error) {
        toast.error(r.error);
        return;
      }
      toast.success("Ingrediente creado");
      setForm({ nombre: "", unidadBase: "UNIDADES", costoUnitario: "", lowStockThreshold: "", brandId: "" });
      setShowForm(false);
      load();
    } catch {
      toast.error("Error al crear ingrediente");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = async () => {
    if (!tid || !editItem) return;
    setSaving(true);
    try {
      const r = (await apiSendJson(
        `/api/ingredients?id=${editItem.id}`,
        "PATCH",
        {
          nombre: editItem.nombre.trim(),
          unidadBase: editItem.unidadBase,
          costoUnitario: editItem.costoUnitario,
          lowStockThreshold: editItem.lowStockThreshold,
          brandId: editItem.brandId || null,
          active: editItem.active,
        },
        tid,
      )) as { ingredient?: Ingredient; error?: string };
      if (r.error) {
        toast.error(r.error);
        return;
      }
      toast.success("Ingrediente actualizado");
      setEditItem(null);
      load();
    } catch {
      toast.error("Error al actualizar ingrediente");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!tid) return;
    try {
      await apiDelete(`/api/ingredients?id=${id}`, tid);
      toast.success("Ingrediente desactivado");
      load();
    } catch {
      toast.error("Error al desactivar");
    }
  };

  const handleAdjust = async (id: string, delta: number, reason: string) => {
    if (!tid) return;
    await apiSendJson(
      `/api/stock/adjustment`,
      "POST",
      {
        ingredientId: id,
        delta,
        reason,
      },
      tid,
    );
    load();
  };

  if (lock) return <FeatureLockedState payload={lock} />;

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const headerActions: QuickAction[] = [
    { key: "new", label: `Nuevo ${copy.itemLabel.toLowerCase()}`, icon: Plus, onClick: () => setShowForm(true) },
    {
      key: "recibir",
      label: "Recibir insumos",
      icon: Inbox,
      variant: "outline",
      href: "/app/compras/?tab=recepciones",
    },
  ];

  const columns: DataTableColumn<Ingredient>[] = [
    { key: "nombre", header: copy.itemLabel, className: "font-medium", render: (item) => item.nombre },
    {
      key: "marca",
      header: "Marca",
      className: "text-muted-foreground",
      render: (item) => item.brandName ?? "—",
    },
    {
      key: "stock",
      header: "Stock actual",
      className: "text-right tabular-nums",
      render: (item) => fmt(item.stockActual, item.unidadBase),
    },
    {
      key: "costo",
      header: "Costo",
      className: "text-right tabular-nums text-muted-foreground",
      render: (item) => `${pesos(item.costoUnitario)}/${UNIT_LABELS[item.unidadBase]}`,
    },
    {
      key: "estado",
      header: "Estado",
      render: (item) => {
        const isLow = item.lowStockThreshold != null && item.stockActual <= item.lowStockThreshold;
        return isLow ? (
          <div className="flex items-center gap-1.5">
            <Badge variant="outline" className="border-amber-500/30 bg-amber-500/10 text-[10px] text-amber-400">
              <AlertTriangle className="mr-1 h-2.5 w-2.5" /> Stock bajo
            </Badge>
            <Link
              to="/app/compras/ordenes"
              search={{ ingredientId: item.id } as never}
              className="text-[10px] font-medium text-primary underline-offset-2 hover:underline"
            >
              Crear OC
            </Link>
          </div>
        ) : (
          <Badge variant="outline" className="border-green-500/30 bg-green-500/10 text-[10px] text-green-400">
            OK
          </Badge>
        );
      },
    },
    {
      key: "adjust",
      header: "Ajuste rápido",
      className: "text-right",
      render: (item) => (
        <div className="flex justify-end gap-1">
          <Button
            size="sm"
            variant="ghost"
            className="h-6 w-6 p-0 text-xs"
            onClick={() => {
              void (async () => {
                const d = await prompt({ title: "Cantidad a ingresar", inputType: "number", confirmLabel: "Ingresar" });
                if (d) handleAdjust(item.id, parseFloat(d), "manual_add");
              })();
            }}
          >
            +
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="h-6 w-6 p-0 text-xs"
            onClick={() => {
              void (async () => {
                const d = await prompt({ title: "Cantidad a retirar", inputType: "number", confirmLabel: "Retirar" });
                if (d) handleAdjust(item.id, -parseFloat(d), "manual_sub");
              })();
            }}
          >
            −
          </Button>
        </div>
      ),
    },
    {
      key: "actions",
      header: "",
      className: "text-right",
      render: (item) => (
        <div className="flex justify-end gap-1">
          <Button
            size="sm"
            variant="ghost"
            className="h-7 w-7 p-0"
            onClick={() => setEditItem({ ...item })}
            title={`Editar ${copy.itemLabel.toLowerCase()}`}
          >
            <Pencil className="h-3.5 w-3.5" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
            onClick={() => {
              void (async () => {
                if (await confirm({ title: `¿Desactivar "${item.nombre}"?`, destructive: true, confirmLabel: "Desactivar" })) void handleDelete(item.id);
              })();
            }}
            title="Desactivar"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      ),
    },
  ];

  const UNIT_OPTIONS: Unit[] = ["GRAMOS", "KILOS", "MILILITROS", "LITROS", "UNIDADES"];

  // ─── EDIT DRAWER ─────────────────────────────────────────────────────────────
  const editDrawer = editItem ? (
    <CrudEditorShell
      open={true}
      onOpenChange={(open) => { if (!open) setEditItem(null); }}
      title={`Editar — ${editItem.nombre}`}
      size="lg"
      list={null}
      footer={
        <>
          <Button type="button" variant="outline" onClick={() => setEditItem(null)} disabled={saving}>
            Cancelar
          </Button>
          <Button type="button" onClick={() => void handleEdit()} disabled={saving || !editItem.nombre.trim()}>
            {saving ? "Guardando..." : "Guardar cambios"}
          </Button>
        </>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5 sm:col-span-2">
          <FieldLabel tipKey="ing.name" required>Nombre</FieldLabel>
          <Input
            value={editItem.nombre}
            onChange={(e) => setEditItem((f) => f ? { ...f, nombre: e.target.value } : f)}
          />
        </div>
        <div className="space-y-1.5">
          <FieldLabel tipKey="ing.brand">Marca (opcional)</FieldLabel>
          <select
            value={editItem.brandId ?? ""}
            onChange={(e) => setEditItem((f) => f ? { ...f, brandId: e.target.value || null } : f)}
            className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
          >
            <option value="">Sin marca</option>
            {brands.map((b) => (
              <option key={b.id} value={b.id}>{b.name}</option>
            ))}
          </select>
        </div>
        <div className="space-y-1.5">
          <FieldLabel tipKey="ing.unit">Unidad base</FieldLabel>
          <select
            value={editItem.unidadBase}
            onChange={(e) => setEditItem((f) => f ? { ...f, unidadBase: e.target.value as Unit } : f)}
            className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
          >
            {UNIT_OPTIONS.map((u) => (
              <option key={u} value={u}>{UNIT_LABELS[u]} ({u})</option>
            ))}
          </select>
        </div>
        <div className="space-y-1.5">
          <FieldLabel tipKey="ing.cost">Costo unitario</FieldLabel>
          <Input
            type="number"
            min={0}
            value={editItem.costoUnitario}
            onChange={(e) => setEditItem((f) => f ? { ...f, costoUnitario: parseFloat(e.target.value) || 0 } : f)}
            className="text-right tabular-nums"
            placeholder="0.00"
          />
        </div>
        <div className="space-y-1.5">
          <FieldLabel tipKey="ing.lowStock">Alerta stock bajo</FieldLabel>
          <Input
            type="number"
            min={0}
            value={editItem.lowStockThreshold ?? ""}
            onChange={(e) => setEditItem((f) => f ? { ...f, lowStockThreshold: e.target.value ? parseFloat(e.target.value) : null } : f)}
            className="text-right tabular-nums"
            placeholder="Sin alerta"
          />
        </div>
        <div className="sm:col-span-2 flex items-center gap-2 rounded-md border border-border/60 bg-muted/30 px-3 py-2 text-sm">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={editItem.active}
              onChange={(e) => setEditItem((f) => f ? { ...f, active: e.target.checked } : f)}
              className="h-4 w-4"
            />
            <span>{copy.activeLabel}</span>
          </label>
        </div>
      </div>
    </CrudEditorShell>
  ) : null;

  return (
    <div>
      <Header title={copy.pageTitle} subtitle={copy.pageSubtitle} actions={<QuickActions actions={headerActions} />} />
      {loading && items.length === 0 ? (
        <Loading variant="list" />
      ) : (
      <ClinicalPageShell offset="none" className="pt-6">
      <WorkspaceHubBack title="Abastecimiento" onBack={() => void navigate({ to: "/app/operaciones" })} />
      {summary.total > 0 && (
        <div className="grid gap-4 sm:grid-cols-3">
          <StatCard label={copy.itemLabelPlural} value={summary.total} icon={Boxes} variant="secondary" />
          <StatCard label="Valor de inventario" value={pesos(summary.totalValue)} icon={Wallet} variant="primary" />
          <StatCard
            label="Stock bajo"
            value={summary.lowStockCount}
            icon={AlertTriangle}
            variant={summary.lowStockCount > 0 ? "warning" : "success"}
          />
        </div>
      )}

      {(summary.total > 0 || brands.length > 0) && (
        <FilterBar search={{ value: query, onChange: setQuery, placeholder: copy.searchPlaceholder }}>
          {brands.length > 0 && (
            <Select value={brandFilter} onValueChange={(v) => setBrandFilter(v)}>
              <SelectTrigger className="w-full sm:w-[200px]">
                <SelectValue placeholder="Marca" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las marcas</SelectItem>
                {brands.map((b) => (
                  <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </FilterBar>
      )}

      {summary.lowStockCount > 0 && (
        <div className="flex items-center gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-400">
          <AlertTriangle className="h-4 w-4 flex-shrink-0" />
          <span>
            {summary.lowStockCount} ingrediente{summary.lowStockCount > 1 ? "s" : ""} con stock bajo
          </span>
        </div>
      )}

      <CrudEditorShell
        open={showForm}
        onOpenChange={(open) => {
          setShowForm(open);
          if (!open) {
            setForm({ nombre: "", unidadBase: "UNIDADES", costoUnitario: "", lowStockThreshold: "", brandId: "" });
          }
        }}
        title={`Nuevo ${copy.itemLabel.toLowerCase()}`}
        size="lg"
        list={
          <DataTable
            columns={columns}
            rows={items}
            getRowKey={(item) => item.id}
            emptyState={
              query.trim() || brandFilter !== "all" ? (
                <EmptyState icon={Inbox} title="Sin resultados" description={`Ningún ${copy.itemLabel.toLowerCase()} coincide con el filtro.`} />
              ) : (
                <EmptyState
                  icon={Inbox}
                  title={copy.emptyTitle}
                  description={copy.emptyDescription}
                />
              )
            }
          />
        }
        footer={
          <>
            <Button type="button" variant="outline" onClick={() => setShowForm(false)} disabled={saving}>
              Cancelar
            </Button>
            <Button type="button" onClick={() => void handleCreate()} disabled={saving}>
              {saving ? "Guardando..." : "Crear"}
            </Button>
          </>
        }
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5 sm:col-span-2">
            <FieldLabel tipKey="ing.name" required>
              Nombre
            </FieldLabel>
            <Input
              value={form.nombre}
              onChange={(e) => setForm((f) => ({ ...f, nombre: e.target.value }))}
              placeholder="Harina 0000"
            />
          </div>
          <div className="space-y-1.5">
            <FieldLabel tipKey="ing.brand">Marca (opcional)</FieldLabel>
            <select
              value={form.brandId}
              onChange={(e) => setForm((f) => ({ ...f, brandId: e.target.value }))}
              className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
            >
              <option value="">Sin marca</option>
              {brands.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1.5">
            <FieldLabel tipKey="ing.unit">Unidad base</FieldLabel>
            <select
              value={form.unidadBase}
              onChange={(e) => setForm((f) => ({ ...f, unidadBase: e.target.value as Unit }))}
              className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
            >
              {(["GRAMOS", "KILOS", "MILILITROS", "LITROS", "UNIDADES"] as Unit[]).map((u) => (
                <option key={u} value={u}>
                  {UNIT_LABELS[u]} ({u})
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1.5">
            <FieldLabel tipKey="ing.cost">Costo unitario</FieldLabel>
            <Input
              type="number"
              min={0}
              value={form.costoUnitario}
              onChange={(e) => setForm((f) => ({ ...f, costoUnitario: e.target.value }))}
              className="text-right tabular-nums"
              placeholder="0.00"
            />
          </div>
          <div className="space-y-1.5">
            <FieldLabel tipKey="ing.lowStock">Alerta stock bajo</FieldLabel>
            <Input
              type="number"
              min={0}
              value={form.lowStockThreshold}
              onChange={(e) => setForm((f) => ({ ...f, lowStockThreshold: e.target.value }))}
              className="text-right tabular-nums"
              placeholder="Opcional"
            />
          </div>
        </div>
      </CrudEditorShell>

      {totalPages > 1 && (
        <Pagination>
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                href="#"
                className={page <= 1 ? "pointer-events-none opacity-50" : ""}
                onClick={(e) => {
                  e.preventDefault();
                  setPage((p) => Math.max(1, p - 1));
                }}
              />
            </PaginationItem>
            {Array.from({ length: Math.min(7, totalPages) }, (_, i) => i + 1).map((p) => (
              <PaginationItem key={p}>
                <PaginationLink
                  href="#"
                  isActive={p === page}
                  onClick={(e) => {
                    e.preventDefault();
                    setPage(p);
                  }}
                >
                  {p}
                </PaginationLink>
              </PaginationItem>
            ))}
            <PaginationItem>
              <PaginationNext
                href="#"
                className={page >= totalPages ? "pointer-events-none opacity-50" : ""}
                onClick={(e) => {
                  e.preventDefault();
                  setPage((p) => Math.min(totalPages, p + 1));
                }}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}
      </ClinicalPageShell>
      )}
      {editDrawer}
    </div>
  );
}
